// doctorProfileSeoData.js
const dayMap = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const specMap = {
  Pneumologji: "Pulmonology",
  Dermatologji: "Dermatology",
  Kardiologji: "Cardiovascular",
  // extend…
};

const slugify = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const isoMinutes = (m) =>
  (Number.isFinite(m) && m > 0) ? `PT${Math.round(m)}M` : undefined;

const extractCity = (loc = {}) => {
  // Prefer explicit city; else try to parse the last token before country from address
  if (loc.city) return loc.city;
  const addr = loc.address || "";
  // e.g. "Rruga …, Tiranë, Albania" -> "Tiranë"
  const parts = addr.split(",").map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    const maybeCity = parts[parts.length - 2];
    // If last looks like country, take previous as city
    if (/albania/i.test(last)) return maybeCity;
  }
  return "Tiranë";
};

const removeEmpty = (obj) => {
  if (Array.isArray(obj)) {
    const arr = obj.map(removeEmpty).filter((v) => v != null && (typeof v !== "object" || Object.keys(v).length));
    return arr.length ? arr : undefined;
  }
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      const cleaned = removeEmpty(v);
      if (cleaned != null && (typeof cleaned !== "object" || (Array.isArray(cleaned) || Object.keys(cleaned).length))) {
        out[k] = cleaned;
      }
    }
    return Object.keys(out).length ? out : undefined;
  }
  return (obj === undefined || obj === null || obj === "") ? undefined : obj;
};

export function buildDoctorSeoData(doctor, opts = {}) {
  const baseUrl = (opts.baseUrl || "https://www.mikdok.com").replace(/\/+$/, "");
  const id = doctor?.id || doctor?.uid || "";
  const name = doctor?.name || "Mjek";
  const loc = doctor?.location || {};
  const city = extractCity(loc);
  const address = loc.address || `${city}, Albania`;
  const coords = loc.coordinates || doctor?.coordinates || {};
  const lat = Number.isFinite(coords.lat) ? coords.lat : undefined;
  const lng = Number.isFinite(coords.lng) ? coords.lng : undefined;
  const spec = doctor?.specialty || "Pneumologji";
  const medicalSpecialty = specMap[spec] || spec;

  // Canonical: include id to avoid duplicates between same-name doctors
  const canonicalUrl = `${baseUrl}/doctor/${slugify(name)}-${slugify(city)}${id ? "-" + slugify(String(id).slice(-6)) : ""}`;

  // Title/description (compact, intent-rich)
  const visibleServices = (doctor?.services || []).map(s => s?.title).filter(Boolean);
  const title = `${name} – ${spec} në ${city} | MikDok`;
  const description =
    `Rezervo online te ${name}, ${spec.toLowerCase()} në ${city}. ` +
    `Shërbime: ${visibleServices.join(", ") || "konsultë"}. ` +
    `Adresë: ${address}.`;

  // Opening hours
  const openingHours = Object.entries(doctor?.workingHours || {})
    .filter(([, v]) => v?.enabled)
    .map(([k, v]) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${dayMap[k]}`,
      opens: v.start,
      closes: v.end,
    }));

  // Offers from services
  const makesOffer = (doctor?.services || []).map((s) => removeEmpty({
    "@type": "Offer",
    price: (s.price != null) ? String(s.price) : undefined,
    priceCurrency: doctor?.currency || "ALL",
    availability: "https://schema.org/InStock",
    itemOffered: {
      "@type": "Service",
      name: s.title || "Shërbim",
      description: Array.isArray(s.keywords) ? s.keywords.join(", ") : undefined,
      hoursAvailable: isoMinutes(s.durationMinutes) ? {
        "@type": "OpeningHoursSpecification",
        duration: isoMinutes(s.durationMinutes),
      } : undefined,
      areaServed: city ? [{ "@type": "City", name: city }] : undefined,
    },
  })).filter(Boolean);

  // Physician JSON-LD
  const physician = removeEmpty({
    "@context": "https://schema.org",
    "@type": "Physician",
    name,
    url: canonicalUrl,
    image: doctor?.photoURL,
    medicalSpecialty,
    address: {
      "@type": "PostalAddress",
      streetAddress: address,
      addressLocality: city,
      addressCountry: "AL",
    },
    geo: (lat != null && lng != null) ? { "@type": "GeoCoordinates", latitude: lat, longitude: lng } : undefined,
    openingHoursSpecification: openingHours,
    makesOffer,
    // Uncomment only if you have real stats
    // aggregateRating: {
    //   "@type": "AggregateRating",
    //   ratingValue: doctor?.rating,
    //   reviewCount: doctor?.reviewCount,
    // },
  });

  // Keywords (merge service keywords + intents + geo + spec)
 // Replace rawKeywords with this:
const addressParts = (address || "")
  .split(",")
  .map(s => s.trim())
  .filter(Boolean);

const serviceTitles = (doctor?.services || [])
  .map(s => s?.title)
  .filter(Boolean);

const serviceKeywords = (doctor?.services || [])
  .flatMap(s => Array.isArray(s?.keywords) ? s.keywords : [])
  .filter(Boolean);

const rawKeywords = [
  name,
  spec,
  city,
  "Albania",
  ...addressParts,
  ...serviceTitles,
  ...serviceKeywords,
]
  .map(x => (x || "").toString().trim())
  .filter(Boolean);

  const seen = new Set();
  const keywords = [];
  for (const k of rawKeywords) {
    const key = k.toLowerCase();
    if (!seen.has(key)) { seen.add(key); keywords.push(k); }
    if (keywords.length >= 60) break; // keep concise
  }

  const breadcrumbs = [
    { name: "MikDok", item: `${baseUrl}/` },
    { name: spec, item: `${baseUrl}/s/${slugify(spec)}` },
    { name, item: canonicalUrl },
  ];

  return {
    title,
    description,
    canonicalUrl,
    keywords,
    doctorJsonLd: physician,
    breadcrumbs,
  };
}
